<!--
 * @Description: 录音功能页面
 * @Date: 2026-02-09
-->
<template>
  <div class="recorder-page">
    <div class="recorder-container">
      <h1 class="title">录音工具</h1>
      
      <!-- 录音状态显示 -->
      <div class="status-box">
        <div class="status-indicator" :class="recordingStatus">
          <span class="status-dot"></span>
          <span class="status-text">{{ statusText }}</span>
        </div>
        <div class="time-display">{{ formattedTime }}</div>
      </div>

      <!-- 波形可视化 -->
      <div class="wave-container">
        <canvas ref="waveCanvas" class="wave-canvas" width="520" height="120"></canvas>
      </div>

      <!-- 控制按钮 -->
      <div class="control-buttons">
        <button 
          v-if="!isRecording && !isPaused" 
          @click="startRecording" 
          class="btn btn-start"
          :disabled="!isSupported"
        >
          <span class="icon">🎤</span>
          <span>开始录音</span>
        </button>

        <button 
          v-if="isRecording" 
          @click="pauseRecording" 
          class="btn btn-pause"
        >
          <span class="icon">⏸</span>
          <span>暂停</span>
        </button>

        <button 
          v-if="isPaused" 
          @click="resumeRecording" 
          class="btn btn-resume"
        >
          <span class="icon">▶️</span>
          <span>继续</span>
        </button>

        <button 
          v-if="isRecording || isPaused" 
          @click="stopRecording" 
          class="btn btn-stop"
        >
          <span class="icon">⏹</span>
          <span>停止</span>
        </button>
      </div>

      <!-- 录音列表 -->
      <div v-if="recordings.length > 0" class="recordings-list">
        <h2 class="list-title">录音列表</h2>
        <div 
          v-for="(recording, index) in recordings" 
          :key="index" 
          class="recording-item"
        >
          <div class="recording-info">
            <div class="recording-name">录音 {{ recordings.length - index }}</div>
            <div class="recording-duration">{{ formatDuration(recording.duration) }}</div>
            <div class="recording-time">{{ recording.timestamp }}</div>
          </div>
          
          <div class="recording-controls">
            <button 
              @click="playRecording(recording, index)" 
              class="btn-icon"
              :class="{ 'active': currentPlaying === index }"
            >
              {{ currentPlaying === index && isPlaying ? '⏸' : '▶️' }}
            </button>
            <button @click="downloadRecording(recording, index)" class="btn-icon">
              💾
            </button>
            <button @click="deleteRecording(index)" class="btn-icon btn-delete">
              🗑️
            </button>
          </div>
        </div>
      </div>

      <!-- 音频播放器（隐藏） -->
      <audio ref="audioPlayer" @ended="onPlayEnded" @pause="onPlayPaused"></audio>

      <!-- 不支持提示 -->
      <div v-if="!isSupported" class="error-message">
        <p>⚠️ 您的浏览器不支持录音功能</p>
        <p>请使用最新版本的 Chrome、Firefox 或 Safari 浏览器</p>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'RecorderPage',
  data() {
    return {
      isSupported: false,
      isRecording: false,
      isPaused: false,
      isPlaying: false,
      currentPlaying: null,
      mediaRecorder: null,
      audioChunks: [],
      stream: null,
      startTime: 0,
      pausedTime: 0,
      elapsedTime: 0,
      timer: null,
      recordings: [],
      audioContext: null,
      analyser: null,
      animationId: null,
    };
  },
  computed: {
    recordingStatus() {
      if (this.isRecording) return 'recording';
      if (this.isPaused) return 'paused';
      return 'idle';
    },
    statusText() {
      if (this.isRecording) return '正在录音...';
      if (this.isPaused) return '已暂停';
      return '就绪';
    },
    formattedTime() {
      return this.formatDuration(this.elapsedTime);
    },
  },
  mounted() {
    this.checkSupport();
    this.loadRecordings();
  },
  beforeDestroy() {
    this.cleanup();
  },
  methods: {
    // 检查浏览器支持
    checkSupport() {
      this.isSupported = !!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia);
    },

    // 开始录音
    async startRecording() {
      try {
        this.stream = await navigator.mediaDevices.getUserMedia({ 
          audio: {
            echoCancellation: true,
            noiseSuppression: true,
            autoGainControl: true,
          } 
        });

        // 创建音频上下文用于可视化
        this.audioContext = new (window.AudioContext || window.webkitAudioContext)();
        const source = this.audioContext.createMediaStreamSource(this.stream);
        this.analyser = this.audioContext.createAnalyser();
        this.analyser.fftSize = 2048;
        source.connect(this.analyser);

        // 开始可视化
        this.visualize();

        // 创建录音器
        const options = { mimeType: 'audio/webm' };
        if (!MediaRecorder.isTypeSupported(options.mimeType)) {
          options.mimeType = 'audio/ogg';
          if (!MediaRecorder.isTypeSupported(options.mimeType)) {
            options.mimeType = 'audio/mp4';
          }
        }

        this.mediaRecorder = new MediaRecorder(this.stream, options);
        this.audioChunks = [];

        this.mediaRecorder.ondataavailable = (event) => {
          if (event.data.size > 0) {
            this.audioChunks.push(event.data);
          }
        };

        this.mediaRecorder.onstop = () => {
          this.saveRecording();
        };

        this.mediaRecorder.start();
        this.isRecording = true;
        this.isPaused = false;
        this.startTime = Date.now() - this.pausedTime;
        this.startTimer();
      } catch (error) {
        console.error('录音启动失败:', error);
        alert('无法访问麦克风，请检查浏览器权限设置');
      }
    },

    // 暂停录音
    pauseRecording() {
      if (this.mediaRecorder && this.mediaRecorder.state === 'recording') {
        this.mediaRecorder.pause();
        this.isRecording = false;
        this.isPaused = true;
        this.pausedTime = this.elapsedTime;
        this.stopTimer();
        this.stopVisualization();
      }
    },

    // 继续录音
    resumeRecording() {
      if (this.mediaRecorder && this.mediaRecorder.state === 'paused') {
        this.mediaRecorder.resume();
        this.isRecording = true;
        this.isPaused = false;
        this.startTime = Date.now() - this.pausedTime;
        this.startTimer();
        this.visualize();
      }
    },

    // 停止录音
    stopRecording() {
      if (this.mediaRecorder) {
        this.mediaRecorder.stop();
        this.isRecording = false;
        this.isPaused = false;
        this.stopTimer();
        this.stopVisualization();
        this.cleanup();
        this.resetTimer();
      }
    },

    // 保存录音
    saveRecording() {
      const blob = new Blob(this.audioChunks, { type: 'audio/webm' });
      const url = URL.createObjectURL(blob);
      const duration = this.elapsedTime;
      const timestamp = new Date().toLocaleString('zh-CN');

      const recording = {
        url,
        blob,
        duration,
        timestamp,
      };

      this.recordings.unshift(recording);
      this.saveToLocalStorage();
    },

    // 播放录音
    playRecording(recording, index) {
      const audio = this.$refs.audioPlayer;
      
      if (this.currentPlaying === index && this.isPlaying) {
        // 暂停当前播放
        audio.pause();
        this.isPlaying = false;
      } else {
        // 播放新录音
        audio.src = recording.url;
        audio.play();
        this.isPlaying = true;
        this.currentPlaying = index;
      }
    },

    // 下载录音
    downloadRecording(recording, index) {
      const a = document.createElement('a');
      a.href = recording.url;
      a.download = `录音_${index + 1}_${recording.timestamp.replace(/[/:]/g, '-')}.webm`;
      a.click();
    },

    // 删除录音
    deleteRecording(index) {
      if (confirm('确定要删除这条录音吗？')) {
        URL.revokeObjectURL(this.recordings[index].url);
        this.recordings.splice(index, 1);
        this.saveToLocalStorage();
      }
    },

    // 播放结束回调
    onPlayEnded() {
      this.isPlaying = false;
      this.currentPlaying = null;
    },

    // 播放暂停回调
    onPlayPaused() {
      this.isPlaying = false;
    },

    // 计时器
    startTimer() {
      this.timer = setInterval(() => {
        this.elapsedTime = Date.now() - this.startTime;
      }, 100);
    },

    stopTimer() {
      if (this.timer) {
        clearInterval(this.timer);
        this.timer = null;
      }
    },

    resetTimer() {
      this.elapsedTime = 0;
      this.pausedTime = 0;
      this.startTime = 0;
    },

    // 格式化时间
    formatDuration(ms) {
      const totalSeconds = Math.floor(ms / 1000);
      const minutes = Math.floor(totalSeconds / 60);
      const seconds = totalSeconds % 60;
      const milliseconds = Math.floor((ms % 1000) / 10);
      return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}.${String(milliseconds).padStart(2, '0')}`;
    },

    // 波形可视化
    visualize() {
      const canvas = this.$refs.waveCanvas;
      if (!canvas) return;

      const canvasCtx = canvas.getContext('2d');
      const WIDTH = canvas.width;
      const HEIGHT = canvas.height;

      const bufferLength = this.analyser.fftSize;
      const dataArray = new Uint8Array(bufferLength);

      const draw = () => {
        this.animationId = requestAnimationFrame(draw);

        this.analyser.getByteTimeDomainData(dataArray);

        canvasCtx.fillStyle = 'rgb(240, 240, 255)';
        canvasCtx.fillRect(0, 0, WIDTH, HEIGHT);

        canvasCtx.lineWidth = 2;
        canvasCtx.strokeStyle = 'rgb(74, 144, 226)';
        canvasCtx.beginPath();

        const sliceWidth = WIDTH / bufferLength;
        let x = 0;

        for (let i = 0; i < bufferLength; i++) {
          const v = dataArray[i] / 128.0;
          const y = (v * HEIGHT) / 2;

          if (i === 0) {
            canvasCtx.moveTo(x, y);
          } else {
            canvasCtx.lineTo(x, y);
          }

          x += sliceWidth;
        }

        canvasCtx.lineTo(WIDTH, HEIGHT / 2);
        canvasCtx.stroke();
      };

      draw();
    },

    // 停止可视化
    stopVisualization() {
      if (this.animationId) {
        cancelAnimationFrame(this.animationId);
        this.animationId = null;
      }

      // 清空画布
      const canvas = this.$refs.waveCanvas;
      if (canvas) {
        const canvasCtx = canvas.getContext('2d');
        canvasCtx.fillStyle = 'rgb(240, 240, 255)';
        canvasCtx.fillRect(0, 0, canvas.width, canvas.height);
      }
    },

    // 清理资源
    cleanup() {
      if (this.stream) {
        this.stream.getTracks().forEach((track) => track.stop());
        this.stream = null;
      }
      if (this.audioContext) {
        this.audioContext.close();
        this.audioContext = null;
      }
    },

    // 保存到本地存储
    saveToLocalStorage() {
      try {
        const data = this.recordings.map(r => ({
          duration: r.duration,
          timestamp: r.timestamp,
        }));
        localStorage.setItem('recordings_meta', JSON.stringify(data));
      } catch (error) {
        console.error('保存到本地存储失败:', error);
      }
    },

    // 从本地存储加载
    loadRecordings() {
      try {
        const data = localStorage.getItem('recordings_meta');
        if (data) {
          // 注意：由于 Blob URL 无法持久化,这里只加载元数据
          // 实际应用中可能需要将录音上传到服务器
          console.log('历史录音元数据:', JSON.parse(data));
        }
      } catch (error) {
        console.error('从本地存储加载失败:', error);
      }
    },
  },
};
</script>

<style lang="less" scoped>
.recorder-page {
  min-height: 100vh;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  padding: 40px 20px;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
}

.recorder-container {
  max-width: 600px;
  margin: 0 auto;
  background: white;
  border-radius: 20px;
  padding: 40px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
}

.title {
  text-align: center;
  color: #333;
  font-size: 32px;
  margin-bottom: 30px;
  font-weight: 600;
}

.status-box {
  background: #f8f9fa;
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 30px;
  text-align: center;
}

.status-indicator {
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 15px;
  font-size: 18px;
  font-weight: 500;
}

.status-dot {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  margin-right: 10px;
  background: #6c757d;
  animation: pulse 2s infinite;
}

.status-indicator.recording .status-dot {
  background: #dc3545;
}

.status-indicator.paused .status-dot {
  background: #ffc107;
  animation: none;
}

.status-indicator.idle .status-dot {
  background: #28a745;
  animation: none;
}

@keyframes pulse {
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.5;
  }
}

.time-display {
  font-size: 36px;
  font-weight: 700;
  color: #495057;
  font-family: 'Courier New', monospace;
}

.wave-container {
  margin-bottom: 30px;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: inset 0 2px 4px rgba(0, 0, 0, 0.1);
}

.wave-canvas {
  width: 100%;
  height: 120px;
  display: block;
}

.control-buttons {
  display: flex;
  justify-content: center;
  gap: 15px;
  margin-bottom: 40px;
  flex-wrap: wrap;
}

.btn {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 14px 28px;
  border: none;
  border-radius: 50px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 6px 16px rgba(0, 0, 0, 0.2);
  }

  &:active:not(:disabled) {
    transform: translateY(0);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  .icon {
    font-size: 20px;
  }
}

.btn-start {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
}

.btn-pause {
  background: linear-gradient(135deg, #f093fb 0%, #f5576c 100%);
  color: white;
}

.btn-resume {
  background: linear-gradient(135deg, #4facfe 0%, #00f2fe 100%);
  color: white;
}

.btn-stop {
  background: linear-gradient(135deg, #fa709a 0%, #fee140 100%);
  color: white;
}

.recordings-list {
  border-top: 2px solid #e9ecef;
  padding-top: 30px;
}

.list-title {
  font-size: 22px;
  color: #333;
  margin-bottom: 20px;
  font-weight: 600;
}

.recording-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: #f8f9fa;
  border-radius: 12px;
  padding: 15px 20px;
  margin-bottom: 15px;
  transition: all 0.3s ease;

  &:hover {
    background: #e9ecef;
    transform: translateX(5px);
  }
}

.recording-info {
  flex: 1;
}

.recording-name {
  font-size: 16px;
  font-weight: 600;
  color: #333;
  margin-bottom: 5px;
}

.recording-duration {
  font-size: 14px;
  color: #6c757d;
  font-family: 'Courier New', monospace;
}

.recording-time {
  font-size: 12px;
  color: #adb5bd;
  margin-top: 3px;
}

.recording-controls {
  display: flex;
  gap: 10px;
}

.btn-icon {
  width: 40px;
  height: 40px;
  border: none;
  border-radius: 50%;
  background: white;
  cursor: pointer;
  font-size: 18px;
  transition: all 0.3s ease;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);

  &:hover {
    transform: scale(1.1);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }

  &.active {
    background: #667eea;
  }

  &.btn-delete:hover {
    background: #dc3545;
  }
}

.error-message {
  background: #fff3cd;
  border: 2px solid #ffc107;
  border-radius: 12px;
  padding: 20px;
  text-align: center;
  color: #856404;

  p {
    margin: 10px 0;
    font-size: 16px;

    &:first-child {
      font-size: 18px;
      font-weight: 600;
    }
  }
}

// 响应式设计
@media (max-width: 768px) {
  .recorder-container {
    padding: 20px;
  }

  .title {
    font-size: 24px;
  }

  .time-display {
    font-size: 28px;
  }

  .btn {
    padding: 12px 20px;
    font-size: 14px;
  }

  .recording-item {
    flex-direction: column;
    align-items: flex-start;
    gap: 15px;
  }

  .recording-controls {
    align-self: flex-end;
  }
}
</style>
